import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { updateMe as updateMeAction } from '../../../actions/user.actions';
import { SubjectsClassified } from '../../../ampli';
import OnboardingStepWichtigkeitImage from '../../../assets/onboarding/onboarding-step-wichtigkeit.svg';
import { routes } from '../../../constants';
import { selectUser } from '../../../reducers/user.selectors';
import type { OnboardingSteps } from '../../../routes/onboarding/OnboardingPage';
import services from '../../../services';
import useAction from '../../../utils/useAction';
import useRouter from '../../../utils/useRouter';
import type { Option } from '../OnboardingOptionChipList';
import OnboardingStepContainer from '../OnboardingStepContainer';
import OnboardingStepImage from '../OnboardingStepImage';
import DraggableSubjectList from './DraggableSubjectList';

interface OwnProps {
	setOnboardingStep: (step: keyof OnboardingSteps) => void;
	initialSelectedSubjects: Option[];
	stepNum?: number;
	totalSteps?: number;
	onBackOverride?: (() => void) | null;
	onNextOverride?: () => void;
	hideImage?: boolean;
	compact?: boolean;
}

const OnboardingStepWichtigkeit: React.FC<OwnProps> = ({
	setOnboardingStep,
	initialSelectedSubjects,
	stepNum,
	totalSteps,
	onBackOverride,
	onNextOverride,
	hideImage,
	compact,
}) => {
	const { t } = useTranslation();
	const { router } = useRouter();
	const user = useSelector(selectUser);
	const updateMe = useAction(updateMeAction);
	const [subjectsOrder, setSubjectsOrder] = useState<Option[]>(initialSelectedSubjects);

	const contentHeights = {
		default: 'calc(100vh - 527px)',
		smallScreen: 'calc(100vh - 353px)',
	};

	if (!initialSelectedSubjects || initialSelectedSubjects.length === 0) {
		setOnboardingStep('schulfaecher');
		return null;
	}

	const handleSubmit = () => {
		if (!user) return;

		const subjects = {
			majors: subjectsOrder.map((subject, i) => ({
				subject: subject.value,
				priority: i,
			})),
			minors: [],
			prioritized: false,
		};

		services.track.ampliEventTrack({
			event: new SubjectsClassified(),
			userProperties: {
				id: user.id,
				subjects,
			},
		});

		if (onNextOverride) {
			updateMe({ subjects }).then(() => onNextOverride());
		} else {
			updateMe({
				subjects,
				data: {
					onboarding: {
						finished: true,
					},
				},
			}).then(() => {
				const destination = user.isAiOnlyUser
					? `/${routes.aiTools.root}`
					: `/${routes.dashboard.root}?isOnboardingComplete=true`;
				router.push(destination);
			});
		}
	};

	return (
		<>
			<OnboardingStepContainer
				title={t('Organize your subjects by importance')}
				subtitle={t(
					'Prioritize your school subjects so we can better personalize the content for you.'
				)}
				stepNum={stepNum ?? 6}
				totalSteps={totalSteps}
				onBack={
					onBackOverride === undefined
						? () => setOnboardingStep('schulfaecher')
						: (onBackOverride ?? undefined)
				}
				onNext={handleSubmit}
				contentHeights={contentHeights}
				compact={compact}
			>
				<DraggableSubjectList items={subjectsOrder} onChange={setSubjectsOrder} />
			</OnboardingStepContainer>
			{!hideImage && <OnboardingStepImage stepImage={<OnboardingStepWichtigkeitImage />} />}
		</>
	);
};

export default OnboardingStepWichtigkeit;
